import { MatchHeader } from '@/components/match/match-header';
import { ExecuteDialog } from '@/components/opportunity/execute-dialog';
import { ObservationTimeline } from '@/components/opportunity/observation-timeline';
import { OddsTradingChart } from '@/components/opportunity/odds-trading-chart';
import { OpportunityCard } from '@/components/opportunity/opportunity-card';
import { TrackedPickCard } from '@/components/opportunity/tracked-pick-card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { trpc } from '@/lib/trpc';
import type { OpportunityRow } from '@tennis-scanner/shared';
import { ChevronLeft } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

// Match Cockpit (architecture § 21.4.2). Drilldown view for ONE match.
// Three tabs:
//   - Alertas: opportunities for this match (full card incl. perBook).
//   - Picks vigilados: tracked signals (also incl. perBook even before
//     they reach value, plus the link to the opp if one exists).
//   - Timeline: observations of the focused opportunity.
//
// V2 (deferred): live score panel from api-tennis livescore. The arch
// doc lists this in §21.4.2 but no endpoint exists yet on our API; the
// connector pulls it for storage but doesn't expose it via tRPC.

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

export function MatchCockpitPage() {
  const { id } = useParams<{ id: string }>();
  const matchId = id ?? '';
  const [executing, setExecuting] = useState<OpportunityRow | null>(null);
  const [selectedOppId, setSelectedOppId] = useState<string | null>(null);

  // Match metadata for the header. matches.search is the only way to
  // fetch a match by id today (no matches.get yet); we use a wide window
  // and filter client-side. Acceptable for the cockpit (occasional read).
  const matchSearch = trpc.matches.search.useQuery(
    {
      limit: 200,
      fromUtc: new Date(Date.now() - ONE_DAY_MS).toISOString(),
      toUtc: new Date(Date.now() + 7 * ONE_DAY_MS).toISOString(),
    },
    { staleTime: 60_000 },
  );
  const match = matchSearch.data?.find((m) => m.id === matchId);

  // staleTime ≥ refetchInterval keeps the cached data "fresh enough" so
  // background refetches don't flip isLoading=true and flash skeletons.
  const tracked = trpc.signals.tracked.useQuery(
    { includeInactive: false, limit: 100 },
    { enabled: matchId.length > 0, refetchInterval: 5000, staleTime: 5000 },
  );
  const opps = trpc.opportunities.list.useQuery(
    { matchId, sortBy: 'rankScore', limit: 50 },
    { enabled: matchId.length > 0, refetchInterval: 5000, staleTime: 5000 },
  );

  // Dedupe across tabs: a tracked signal that already has an opportunity
  // is shown ONLY in the Alertas tab. Mirrors the dashboard's filter
  // (dashboard.tsx) so the operator never sees the same pick twice.
  const trackedForMatch = useMemo(
    () =>
      (tracked.data ?? []).filter(
        (t) => t.signal.canonicalMatchId === matchId && t.opportunityId === null,
      ),
    [tracked.data, matchId],
  );

  const focusOpp = useMemo(() => {
    const rows = opps.data ?? [];
    if (selectedOppId) return rows.find((o) => o.id === selectedOppId) ?? rows[0] ?? null;
    return rows[0] ?? null;
  }, [opps.data, selectedOppId]);

  const oppDetail = trpc.opportunities.get.useQuery(
    { id: focusOpp?.id ?? '' },
    { enabled: !!focusOpp, refetchInterval: 5000, staleTime: 5000 },
  );

  if (!matchId) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Match no encontrado</AlertTitle>
        <AlertDescription>La URL no incluye id.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <Link
        to="/dashboard"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="size-3" />
        Dashboard
      </Link>
      <span data-testid="page-description" className="sr-only">
        Match cockpit for {matchId}
      </span>

      {matchSearch.isLoading ? (
        <Skeleton className="h-20" />
      ) : (
        <MatchHeader match={match} fallbackId={matchId} />
      )}

      <Tabs defaultValue="opportunities" className="space-y-4">
        <TabsList>
          <TabsTrigger value="opportunities">Alertas ({opps.data?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="picks">Picks vigilados ({trackedForMatch.length})</TabsTrigger>
          <TabsTrigger value="timeline">Movimiento de cuotas</TabsTrigger>
        </TabsList>

        <TabsContent value="opportunities" className="space-y-3">
          {opps.isLoading && <Skeleton className="h-32" />}
          {!opps.isLoading && (opps.data?.length ?? 0) === 0 && (
            <EmptyHint title="Sin alertas con value en este partido">
              Las alertas aparecen cuando alguna casa paga ≥ tu cuota mínima. Mientras tanto, mirá
              "Picks vigilados" para ver el estado actual.
            </EmptyHint>
          )}
          {(opps.data ?? []).map((o) => (
            <OpportunityCard
              key={o.id}
              opportunity={o}
              onExecuteClick={setExecuting}
              onSelect={(opp) => setSelectedOppId(opp.id)}
            />
          ))}
        </TabsContent>

        <TabsContent value="picks" className="space-y-3">
          {tracked.isLoading && <Skeleton className="h-32" />}
          {!tracked.isLoading && trackedForMatch.length === 0 && (
            <EmptyHint title="Sin picks vigilados para este partido">
              Cargá un pick manual desde la página "Cargar pick" para empezar a rastrearlo.
            </EmptyHint>
          )}
          {trackedForMatch.map((t) => (
            <TrackedPickCard key={t.signal.id} tracked={t} />
          ))}
        </TabsContent>

        <TabsContent value="timeline" className="space-y-3">
          {/* Trading chart reads from odds_ticks (TimescaleDB), so it works
              for ANY match in the radar — not just those with an
              opportunity row. We render it whenever we have a matchId. */}
          <OddsTradingChart
            matchId={matchId}
            matchStartsAt={match?.startsAt}
            pick={
              focusOpp
                ? {
                    selection: focusOpp.selection as 'p1' | 'p2',
                    targetOdds: focusOpp.targetOdds,
                    p1Name: match?.p1Name,
                    p2Name: match?.p2Name,
                  }
                : undefined
            }
          />
          {focusOpp && oppDetail.data && oppDetail.data.observations.length > 0 && (
            <details className="rounded-lg border border-border bg-card p-4">
              <summary className="cursor-pointer text-sm font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground">
                Ver eventos del scanner ({oppDetail.data.observations.length})
              </summary>
              <div className="mt-3">
                <ObservationTimeline observations={oppDetail.data.observations} />
              </div>
            </details>
          )}
        </TabsContent>
      </Tabs>

      <ExecuteDialog opportunity={executing} onClose={() => setExecuting(null)} />
    </div>
  );
}

function EmptyHint({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-card py-8 text-center">
      <p className="text-sm font-medium text-foreground">{title}</p>
      {children && <p className="max-w-md text-xs text-muted-foreground">{children}</p>}
    </div>
  );
}
